package redmcrosco.alex.farmatch.voice

import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.minus
import kotlinx.datetime.plus

/**
 * "Agente" vocale locale: nessun modello, nessuna chiamata di rete, nessun costo.
 * Trasforma il testo trascritto (da SpeechRecognizer su Android o dalla Web Speech API sul
 * browser) in un [VoiceIntent] strutturato con gli stessi campi che l'utente compilerebbe a
 * mano nei form esistenti (creazione turno, ricerca farmacisti/farmacie).
 *
 * Non sostituisce la tastiera: il risultato va sempre mostrato pre-compilato nel form esistente
 * per conferma/modifica prima dell'invio, non inviato direttamente.
 */
sealed class VoiceIntent {
    data class CreaTurno(
        val data: LocalDate?,
        val oraInizio: String?,
        val oraFine: String?,
        val compenso: Double?,
        val zonaTestuale: String?,
        /**
         * Non null se l'utente ha chiesto un intervallo ("dal 11 al 30", "prossima settimana",
         * "questo mese", "quest'anno"...): [data] è l'inizio dell'intervallo.
         */
        val dataFine: LocalDate? = null,
        /**
         * Se non vuoto, filtra l'intervallo [data]..[dataFine] solo su questi giorni della
         * settimana (es. "lunedì mercoledì e giovedì"). Vuoto = tutti i giorni dell'intervallo.
         */
        val giorniSettimana: Set<DayOfWeek> = emptySet(),
        /**
         * Non null solo per combinazioni di settimane del mese NON adiacenti (es. "1 e 3
         * settimana di ottobre", saltando la 2): un unico intervallo [data]..[dataFine] non
         * basterebbe a rappresentarle senza includere anche i giorni di mezzo non richiesti.
         * Quando presente, [data]/[dataFine] non vanno usati per generare le date.
         */
        val dateEsplicite: List<LocalDate>? = null
    ) : VoiceIntent()

    data class CercaFarmacisti(
        val comune: String?,
        val provincia: String?,
        val query: String?
    ) : VoiceIntent()

    data class CercaFarmacie(
        val comune: String?,
        val provincia: String?,
        val query: String?,
        val regione: String? = null
    ) : VoiceIntent()

    /**
     * Conferma esplicita per eseguire un'azione già mostrata a schermo (pubblicare nuove date,
     * o eseguire una cancellazione/modifica su annunci esistenti già identificati): "pubblica",
     * "pubblica le date", "conferma". Riconosciuto solo se la frase non menziona anche
     * "turno/turni" (altrimenti è una richiesta di creazione, non una conferma).
     * Cosa succede alla conferma dipende dal contesto in cui viene ascoltata (la UI decide).
     */
    object Conferma : VoiceIntent()

    /**
     * "apri maps", "apri la mappa", "portami lì", "naviga": apre Google Maps sull'indirizzo del
     * primo risultato dell'ultima ricerca farmacie mostrata a schermo. La UI decide cosa "primo
     * risultato" significa (in genere il primo della lista attualmente filtrata).
     */
    object ApriMappa : VoiceIntent()

    enum class AzioneAnnunci { CANCELLA, MODIFICA }

    /**
     * Comando su annunci GIÀ pubblicati (non sulla creazione di uno nuovo): "cancella il turno
     * del 15 ottobre", "modifica il turno di domani il compenso a 30 euro", "cancella tutti i
     * turni di novembre". NON esegue nulla da solo: la UI deve prima trovare e mostrare gli
     * annunci corrispondenti ai criteri, e solo un successivo [Conferma] esplicito esegue
     * davvero l'azione — stesso principio "mostra prima, esegui poi" della creazione.
     */
    data class GestisciAnnunci(
        val azione: AzioneAnnunci,
        val data: LocalDate?,
        val dataFine: LocalDate?,
        val giorniSettimana: Set<DayOfWeek>,
        /** true se l'utente ha detto esplicitamente "tutti/tutto" senza altri criteri di data. */
        val tutti: Boolean,
        /** Solo per [AzioneAnnunci.MODIFICA]: nuovi valori (null = non cambiare quel campo). */
        val nuovoOraInizio: String? = null,
        val nuovoOraFine: String? = null,
        val nuovoCompenso: Double? = null
    ) : VoiceIntent()

    /** Il testo non è stato riconosciuto: la UI deve chiedere di ripetere o passare alla tastiera. */
    data class Unrecognized(val rawText: String) : VoiceIntent()
}

object VoiceCommandParser {

    // Verbi di ricerca: bastano parole isolate (non frasi intere), per tollerare il modo
    // naturale in cui le persone parlano ("cerca farmacia Bologna", non necessariamente
    // "cerca una farmacia a Bologna").
    private val cercaVerbi = listOf("cerca", "cercami", "trova", "trovami", "mostrami", "fammi vedere")
    private val creaTurnoVerbi = listOf("crea", "crei", "aggiungi", "pubblica", "inserisci", "nuovo", "nuova")
    // Include anche i verbi di cancella/modifica e un "sì": dopo aver identificato dei turni da
    // cancellare/modificare, l'utente spesso ripete solo il verbo ("cancella", "elimina") invece
    // di dire proprio "conferma" — devono valere entrambi come conferma della stessa azione.
    private val confermaVerbi = listOf(
        "pubblica", "pubblicare", "conferma", "confermo", "procedi",
        "cancella", "cancellare", "cancelli", "elimina", "eliminare", "elimini",
        "si", "va bene", "ok"
    )
    // Verbi per agire su annunci GIÀ pubblicati: controllati con priorità più alta della
    // creazione, altrimenti "cancella il turno del 15" verrebbe letto come "crea un turno il 15".
    // NON richiedono la parola "turno/turni" esplicita: "modifica l'orario del 3 dicembre" è
    // comune quanto "modifica il turno del 3 dicembre".
    // Includono anche le forme coniugate ("mi cancelli", "mi modifichi", "puoi eliminarmi..."):
    // molto comuni quando ci si rivolge all'assistente in modo indiretto/cortese.
    private val cancellaVerbi = listOf("cancella", "cancellare", "cancelli", "elimina", "eliminare", "elimini", "rimuovi", "togli")
    private val modificaVerbi = listOf("modifica", "modificare", "modifichi", "cambia", "cambiare", "cambi", "aggiorna")
    // Frasi per aprire Google Maps sul primo risultato dell'ultima ricerca farmacie.
    private val apriMappaFrasi = listOf(
        "apri maps", "apri la mappa", "apri google maps", "apri il navigatore",
        "portami li", "portami là", "portami lì", "naviga"
    )
    private val turnoRegex = Regex("""\bturn[oi]\b""")
    private val farmacistiRegex = Regex("farmacist")   // farmacista/farmacisti
    private val farmacieRegex = Regex("farmacia|farmacie")

    // Chiave = come lo pronuncia/trascrive lo STT (minuscolo, spazi al posto di trattini/apostrofi,
    // già normalizzato); valore = nome canonico atteso dal filtro regione lato server.
    private val regioniMap = mapOf(
        "emilia romagna" to "Emilia-Romagna",
        "friuli venezia giulia" to "Friuli-Venezia Giulia",
        "trentino alto adige" to "Trentino-Alto Adige",
        "valle d aosta" to "Valle d'Aosta",
        "abruzzo" to "Abruzzo",
        "basilicata" to "Basilicata",
        "calabria" to "Calabria",
        "campania" to "Campania",
        "lazio" to "Lazio",
        "liguria" to "Liguria",
        "lombardia" to "Lombardia",
        "marche" to "Marche",
        "molise" to "Molise",
        "piemonte" to "Piemonte",
        "puglia" to "Puglia",
        "sardegna" to "Sardegna",
        "sicilia" to "Sicilia",
        "toscana" to "Toscana",
        "umbria" to "Umbria",
        "veneto" to "Veneto"
    )

    private val giorniSettimana = mapOf(
        "lunedì" to DayOfWeek.MONDAY, "lunedi" to DayOfWeek.MONDAY,
        "martedì" to DayOfWeek.TUESDAY, "martedi" to DayOfWeek.TUESDAY,
        "mercoledì" to DayOfWeek.WEDNESDAY, "mercoledi" to DayOfWeek.WEDNESDAY,
        "giovedì" to DayOfWeek.THURSDAY, "giovedi" to DayOfWeek.THURSDAY,
        "venerdì" to DayOfWeek.FRIDAY, "venerdi" to DayOfWeek.FRIDAY,
        "sabato" to DayOfWeek.SATURDAY,
        "domenica" to DayOfWeek.SUNDAY
    )

    private val mesi = mapOf(
        "gennaio" to 1, "febbraio" to 2, "marzo" to 3, "aprile" to 4, "maggio" to 5, "giugno" to 6,
        "luglio" to 7, "agosto" to 8, "settembre" to 9, "ottobre" to 10, "novembre" to 11, "dicembre" to 12
    )

    // -1 = ultima settimana del mese (lunghezza variabile in base ai giorni del mese)
    private val ordinaliParola = mapOf(
        "prima" to 1, "seconda" to 2, "terza" to 3, "quarta" to 4, "ultima" to -1
    )
    // Token di un ordinale settimana: parola ("seconda") oppure cifra 1-4 ("2"), quest'ultima
    // isolata (non parte di un numero più lungo) per non intercettare cifre di altro tipo.
    private val ordinaleToken = "(?:" + ordinaliParola.keys.joinToString("|") + """|(?<!\d)[1-4](?!\d))"""

    // Numeri da 0 a 59 scritti in lettere (coprono ore 0-23 e minuti 0-59): lo speech-to-text a
    // volte scrive "otto venti" invece di "8:20", specialmente quando cadono anche le parole
    // "dalle"/"alle". Convertiamo tutto in cifre PRIMA di ogni altra estrazione, così data,
    // orario e compenso funzionano allo stesso modo sia che i numeri arrivino come cifre sia
    // come parole.
    private val paroleNumeriche: Map<String, Int> = buildMap {
        val unita = listOf(
            "zero" to 0, "uno" to 1, "due" to 2, "tre" to 3, "quattro" to 4, "cinque" to 5,
            "sei" to 6, "sette" to 7, "otto" to 8, "nove" to 9
        )
        val teens = listOf(
            "dieci" to 10, "undici" to 11, "dodici" to 12, "tredici" to 13, "quattordici" to 14,
            "quindici" to 15, "sedici" to 16, "diciassette" to 17, "diciotto" to 18, "diciannove" to 19
        )
        (unita + teens).forEach { (parola, valore) -> put(parola, valore) }
        val decineRadici = listOf("venti" to 20, "trenta" to 30, "quaranta" to 40, "cinquanta" to 50)
        decineRadici.forEach { (radice, base) ->
            put(radice, base)
            unita.drop(1).forEach { (paroleUnita, valoreUnita) -> // salta "zero"
                // Elisione della vocale finale davanti a parole che iniziano per vocale (uno, otto)
                val radiceElisa = if (paroleUnita.first() in "aeiou") radice.dropLast(1) else radice
                put("$radiceElisa$paroleUnita", base + valoreUnita)
            }
            // Variante con accento per il "3" finale (ventitré, trentatré...), oltre a quella comune senza accento già coperta sopra
            put("${radice.dropLast(1)}tré", base + 3)
        }
    }

    private fun convertiNumeriParola(text: String): String {
        var result = text
        paroleNumeriche.entries.sortedByDescending { it.key.length }.forEach { (parola, valore) ->
            result = result.replace(Regex("(?<![a-zàèéìòù])$parola(?![a-zàèéìòù])"), valore.toString())
        }
        return result
    }

    fun parse(rawText: String, today: LocalDate): VoiceIntent {
        val text = normalize(rawText)
        if (text.isBlank()) return VoiceIntent.Unrecognized(rawText)

        val giorniMenzionati = extractGiorniMenzionati(text)

        // Priorità: intervallo esplicito "dal X al Y" > "prima/seconda/... settimana [di mese]"
        // (anche combinate: "seconda e terza", "1 e 3") > anno > mese intero > settimana generica
        // > più giorni nominati senza intervallo esplicito (assume i prossimi 7 giorni come base)
        // > data singola. L'ordine conta: "prima settimana di ottobre" contiene il nome del mese,
        // quindi il controllo della settimana specifica deve venire PRIMA di quello del mese intero.
        val explicitDalAl = extractDateRange(text, today)
        val giorniMultipliMese = if (explicitDalAl == null) extractGiorniMultipliMese(text, today) else null
        val settimaneDelMese = if (explicitDalAl == null && giorniMultipliMese == null) extractWeekOfMonthRanges(text, today) else null
        val (rangeDaSettimane, dateEsplicite) = if (settimaneDelMese != null) mergeSettimane(settimaneDelMese) else null to null

        val range: Pair<LocalDate, LocalDate>? = when {
            explicitDalAl != null -> explicitDalAl
            giorniMultipliMese != null -> giorniMultipliMese
            settimaneDelMese != null -> rangeDaSettimane // se null qui, ci sono "buchi": si usa dateEsplicite più sotto
            else ->
                extractYearRange(text, today)
                    ?: extractMonthRange(text, today)
                    ?: extractWeekRange(text, today)
                    ?: if (giorniMenzionati.size >= 2) today.plus(1, DateTimeUnit.DAY) to today.plus(7, DateTimeUnit.DAY) else null
        }

        val data = range?.first ?: dateEsplicite?.minOrNull() ?: extractDate(text, today)
        val dataFine = range?.second
        val (oraInizio, oraFine) = extractTimeRange(text)
        val compenso = extractCompenso(text)
        val zona = extractLocationCandidate(text)

        val haCercaVerbo = cercaVerbi.any { text.contains(it) }
        val menzionaFarmacisti = farmacistiRegex.containsMatchIn(text)
        val menzionaFarmacie = farmacieRegex.containsMatchIn(text)
        val menzionaTurno = turnoRegex.containsMatchIn(text)

        // Criteri di ricerca farmacie: provincia/regione hanno priorità sul comune, perché sono
        // più specifici e si escludono a vicenda ("in provincia di Bergamo" non deve anche
        // risultare in comune = "provincia di Bergamo"). Il comune vero e proprio viene estratto
        // SOLO con preposizione esplicita ("a Milano", "zona Monza", "in Savona"): la parola detta
        // subito dopo "farmacia/farmacie" SENZA preposizione né marcatore di nome ("cerca farmacia
        // Spinaci", "cerca farmacia Savona") va invece nel campo `query`, perché lato server quel
        // campo cerca sia nel nome della farmacia sia nella città — non serve (né è possibile,
        // senza un elenco di comuni offline) indovinare qui se la parola è un nome proprio o una
        // città: la ricerca combinata copre comunque entrambi i casi.
        val provinciaRicerca = extractProvincia(text)
        val regioneRicerca = if (provinciaRicerca == null) extractRegione(text) else null
        val comuneFarmacie = if (provinciaRicerca == null && regioneRicerca == null) extractComuneEsplicito(text) else null
        val queryFarmacia = if (provinciaRicerca == null && regioneRicerca == null && comuneFarmacie == null) {
            extractNomeFarmacia(text) ?: extractParolaDopoFarmacia(text)
        } else null
        // Per i farmacisti non c'è un campo combinato nome+comune (il `query` lato server cerca
        // solo nome/cognome del farmacista): la parola senza preposizione resta quindi un comune,
        // interpretazione di gran lunga più comune nel parlato ("cerca farmacisti Monza").
        val comuneFarmacisti = extractComuneEsplicito(text) ?: extractParolaDopoFarmacista(text) ?: zona
        val haCreaTurnoVerbo = creaTurnoVerbi.any { text.contains(it) }
        val haCancellaVerbo = cancellaVerbi.any { text.contains(it) }
        val haModificaVerbo = modificaVerbi.any { text.contains(it) }
        val tutti = text.contains("tutti") || text.contains("tutto")
        val hasCriterioAnnunci = data != null || dataFine != null || giorniMenzionati.isNotEmpty() || tutti

        return when {
            // "cerca farmacisti", "trova un farmacista a Monza", "cerca farmacisti Savona", ecc.
            haCercaVerbo && menzionaFarmacisti ->
                VoiceIntent.CercaFarmacisti(comune = comuneFarmacisti, provincia = provinciaRicerca, query = null)

            // "cerca farmacia Bologna", "cerca farmacia Spinaci" (nome), "trova le farmacie a
            // Brescia", "cerca farmacie in provincia di Bergamo", "cerca farmacie in Lombardia",
            // "cerca farmacia di nome Santa Rita", ecc.
            haCercaVerbo && menzionaFarmacie ->
                VoiceIntent.CercaFarmacie(
                    comune = comuneFarmacie, provincia = provinciaRicerca, query = queryFarmacia,
                    regione = regioneRicerca
                )

            // "cancella/elimina il turno del 15 ottobre", "cancella tutti i turni di novembre",
            // "elimina l'annuncio del 3 dicembre": priorità sulla creazione, altrimenti "cancella
            // il turno del 15" verrebbe letto come "crea un turno il 15". Non richiede la parola
            // "turno" (si dice spesso "cancella il 15" o "cancella l'annuncio del 15"). Serve un
            // criterio esplicito (data/settimana/mese o "tutti"), altrimenti una "cancella i
            // turni" troppo generica non deve poter colpire tutto.
            haCancellaVerbo && hasCriterioAnnunci ->
                VoiceIntent.GestisciAnnunci(VoiceIntent.AzioneAnnunci.CANCELLA, data, dataFine, giorniMenzionati, tutti)

            // "modifica il turno di domani il compenso a 30 euro", "modifica l'orario del 3
            // dicembre" — anche qui non richiede la parola "turno" esplicita.
            haModificaVerbo && hasCriterioAnnunci ->
                VoiceIntent.GestisciAnnunci(
                    VoiceIntent.AzioneAnnunci.MODIFICA, data, dataFine, giorniMenzionati, tutti,
                    oraInizio, oraFine, compenso
                )

            // "crea un turno...", ma anche solo "turno domani dalle 8 alle 20" (basta che si
            // riconosca almeno una data o un orario, senza per forza un verbo esplicito)
            menzionaTurno && (haCreaTurnoVerbo || data != null || oraInizio != null || dateEsplicite != null) ->
                VoiceIntent.CreaTurno(data, oraInizio, oraFine, compenso, zona, dataFine, giorniMenzionati, dateEsplicite)

            // "apri maps", "apri la mappa", "portami lì", "naviga": apre Google Maps sul primo
            // risultato dell'ultima ricerca farmacie mostrata a schermo.
            apriMappaFrasi.any { text.contains(it) } -> VoiceIntent.ApriMappa

            // "pubblica", "pubblica le date", "conferma": conferma quanto già compilato/trovato.
            // Solo se NON menziona "turno/turni" (altrimenti è una richiesta gestita sopra).
            !menzionaTurno && confermaVerbi.any { text.contains(it) } -> VoiceIntent.Conferma

            else -> VoiceIntent.Unrecognized(rawText)
        }
    }

    /**
     * Estrae TUTTI i giorni della settimana nominati (non solo il primo), es. "lunedì mercoledì
     * e giovedì". Usa `contains` semplice (non `\b`): con le vocali accentate (ì) il boundary
     * `\b` di default in JVM/JS non si comporta in modo affidabile.
     */
    private fun extractGiorniMenzionati(text: String): Set<DayOfWeek> {
        val trovati = mutableSetOf<DayOfWeek>()
        for ((nome, giorno) in giorniSettimana) {
            if (text.contains(nome)) trovati.add(giorno)
        }
        return trovati
    }

    /** "prossima settimana" -> lunedì..domenica della settimana successiva; "una settimana di turni" -> i prossimi 7 giorni. */
    private fun extractWeekRange(text: String, today: LocalDate): Pair<LocalDate, LocalDate>? {
        if (!text.contains("settimana")) return null

        val prossima = text.contains("prossima settimana") || text.contains("settimana prossima")
        if (!prossima) {
            // "questa settimana" o genericamente "una settimana di turni": i prossimi 7 giorni da
            // domani (mai giorni passati, a differenza di un lunedì-domenica di calendario fisso).
            return today.plus(1, DateTimeUnit.DAY) to today.plus(7, DateTimeUnit.DAY)
        }

        // Lunedì della settimana corrente (anche se è nel passato rispetto a "oggi"), poi +7 per
        // arrivare al lunedì della settimana successiva.
        var lunediSettimanaCorrente = today
        while (lunediSettimanaCorrente.dayOfWeek != DayOfWeek.MONDAY) {
            lunediSettimanaCorrente = lunediSettimanaCorrente.minus(1, DateTimeUnit.DAY)
        }
        val lunediProssimo = lunediSettimanaCorrente.plus(7, DateTimeUnit.DAY)
        return lunediProssimo to lunediProssimo.plus(6, DateTimeUnit.DAY)
    }

    /**
     * "questo mese", "mese prossimo"/"prossimo mese", oppure il nome di un mese ("a marzo", "il
     * mese di aprile") inteso come mese intero — non confuso con una data esplicita "15 marzo"
     * (già gestita da [extractDate] / [extractDateRange]).
     */
    private fun extractMonthRange(text: String, today: LocalDate): Pair<LocalDate, LocalDate>? {
        if (text.contains("questo mese")) {
            return today to lastDayOfMonth(today.year, today.monthNumber)
        }
        if (text.contains("mese prossimo") || text.contains("prossimo mese")) {
            var y = today.year
            var m = today.monthNumber + 1
            if (m > 12) { m = 1; y += 1 }
            return LocalDate(y, m, 1) to lastDayOfMonth(y, m)
        }
        for ((nome, numero) in mesi) {
            if (!text.contains(nome)) continue
            // Se il nome del mese è preceduto da un numero di giorno ("15 marzo"), è una data
            // esplicita, non un mese intero: lasciamo gestire quel caso a extractDate/extractDateRange.
            if (Regex("""\d{1,2}\s+$nome""").containsMatchIn(text)) continue

            var y = today.year
            var end = lastDayOfMonth(y, numero)
            if (end < today) { y += 1; end = lastDayOfMonth(y, numero) }
            val start = LocalDate(y, numero, 1)
            return (if (start < today) today else start) to end
        }
        return null
    }

    /**
     * Estrae TUTTI i numeri di settimana nominati, anche coordinati con una sola occorrenza
     * della parola "settimana" ("seconda e terza settimana", "1 e 3 settimana", "prima, seconda
     * e quarta settimana"). Ordine di apparizione, senza duplicati.
     */
    private fun extractOrdinaliSettimana(text: String): List<Int> {
        val match = Regex("""((?:$ordinaleToken[\s,e]*)+)settimana""").find(text) ?: return emptyList()
        val segmento = match.groupValues[1]
        val trovati = mutableListOf<Int>()
        Regex(ordinaleToken).findAll(segmento).forEach { tok ->
            val valore = ordinaliParola[tok.value] ?: tok.value.toIntOrNull()
            if (valore != null && valore !in trovati) trovati.add(valore)
        }
        return trovati
    }

    /**
     * "prima/seconda/terza/quarta/ultima settimana [di <mese>]", anche con più settimane
     * combinate ("seconda e terza settimana", "1 e 3 settimana"): più specifico del mese intero,
     * va controllato PRIMA di [extractMonthRange] perché la frase contiene comunque il nome del
     * mese (es. "prima settimana di ottobre" non deve diventare "tutto ottobre").
     * Le settimane sono blocchi di 7 giorni di calendario (1-7, 8-14, 15-21, 22-28, ultimi 7),
     * non necessariamente allineati a lunedì-domenica.
     */
    private fun extractWeekOfMonthRanges(text: String, today: LocalDate): List<Pair<LocalDate, LocalDate>>? {
        val ordinali = extractOrdinaliSettimana(text)
        if (ordinali.isEmpty()) return null

        var year = today.year
        var month = today.monthNumber
        var meseEsplicito = false
        for ((nome, numero) in mesi) {
            if (text.contains(nome)) { month = numero; meseEsplicito = true; break }
        }

        fun weekRangeInMonth(y: Int, m: Int, ordinale: Int): Pair<LocalDate, LocalDate> {
            val giorniNelMese = lastDayOfMonth(y, m).dayOfMonth
            val (startDay, endDay) = if (ordinale == -1) {
                (giorniNelMese - 6).coerceAtLeast(1) to giorniNelMese
            } else {
                val s = (ordinale - 1) * 7 + 1
                s to (s + 6).coerceAtMost(giorniNelMese)
            }
            return LocalDate(y, m, startDay) to LocalDate(y, m, endDay)
        }

        fun ranges(y: Int, m: Int) = ordinali.map { weekRangeInMonth(y, m, it) }.sortedBy { it.first }

        var result = ranges(year, month)

        // Nessun mese esplicito e il periodo è già passato questo mese: prova il mese prossimo.
        if (!meseEsplicito && result.last().second < today) {
            month += 1
            if (month > 12) { month = 1; year += 1 }
            result = ranges(year, month)
        }

        return result
    }

    /**
     * Unisce più intervalli settimana-del-mese: se sono adiacenti (nessun giorno "di mezzo" non
     * richiesto), diventano un unico intervallo continuo; se ci sono buchi (es. settimana 1 e 3,
     * saltando la 2), non è rappresentabile come un solo intervallo e si esplodono le date una
     * per una nel secondo elemento della coppia restituita.
     */
    private fun mergeSettimane(ranges: List<Pair<LocalDate, LocalDate>>): Pair<Pair<LocalDate, LocalDate>?, List<LocalDate>?> {
        if (ranges.size == 1) return ranges[0] to null
        val ordinati = ranges.sortedBy { it.first }
        val adiacenti = (1 until ordinati.size).all { i -> ordinati[i].first == ordinati[i - 1].second.plus(1, DateTimeUnit.DAY) }
        if (adiacenti) return (ordinati.first().first to ordinati.last().second) to null

        val date = ordinati.flatMap { (inizio, fine) ->
            generateSequence(inizio) { it.plus(1, DateTimeUnit.DAY) }.takeWhile { it <= fine }
        }
        return null to date
    }

    /** "quest'anno"/"tutto l'anno" -> da oggi al 31/12; "l'anno prossimo"/"prossimo anno" -> anno solare successivo. */
    private fun extractYearRange(text: String, today: LocalDate): Pair<LocalDate, LocalDate>? {
        if (text.contains("anno prossimo") || text.contains("prossimo anno")) {
            val y = today.year + 1
            return LocalDate(y, 1, 1) to LocalDate(y, 12, 31)
        }
        if (text.contains("quest anno") || text.contains("questo anno") || text.contains("tutto l anno") || text.contains("per l anno")) {
            return today to LocalDate(today.year, 12, 31)
        }
        return null
    }

    private fun lastDayOfMonth(year: Int, month: Int): LocalDate {
        val firstOfNext = if (month == 12) LocalDate(year + 1, 1, 1) else LocalDate(year, month + 1, 1)
        return firstOfNext.minus(1, DateTimeUnit.DAY)
    }

    private fun normalize(text: String): String {
        val pulito = text.trim().lowercase()
            // Non tocca la virgola/punto decimale tra cifre (es. "22,50 euro")
            .replace(Regex("(?<![0-9])[.,;!?](?![0-9])"), " ")
            // Le elisioni ("quest'anno", "dall'11", "dell'anno") diventano due parole separate:
            // più facili da matchare in modo uniforme, indipendentemente da come le trascrive lo STT.
            .replace("'", " ")
            .replace(Regex("\\s+"), " ")
            .trim()
        return convertiNumeriParola(pulito)
    }

    /**
     * Riconosce: "oggi", "domani", "dopodomani", nomi di giorni della settimana
     * (eventualmente con "prossimo"), date esplicite "15 marzo" / "15/03" / "15-03-2026".
     */
    private fun extractDate(text: String, today: LocalDate): LocalDate? {
        if (text.contains("dopodomani")) return today.plus(2, DateTimeUnit.DAY)
        if (text.contains("oggi")) return today
        if (text.contains("domani")) return today.plus(1, DateTimeUnit.DAY)

        // Data esplicita numerica: 15/03, 15-03, 15/03/2026
        Regex("""(\d{1,2})[/\-](\d{1,2})(?:[/\-](\d{2,4}))?""").find(text)?.let { m ->
            val day = m.groupValues[1].toIntOrNull()
            val month = m.groupValues[2].toIntOrNull()
            val yearRaw = m.groupValues[3].takeIf { it.isNotBlank() }?.toIntOrNull()
            val year = when {
                yearRaw == null -> today.year
                yearRaw < 100 -> 2000 + yearRaw
                else -> yearRaw
            }
            if (day != null && month != null) {
                runCatching { LocalDate(year, month, day) }.getOrNull()?.let { return it }
            }
        }

        // Data esplicita testuale: "15 marzo", "3 aprile 2026"
        Regex("""(\d{1,2})\s+(""" + mesi.keys.joinToString("|") + """)(?:\s+(\d{4}))?""").find(text)?.let { m ->
            val day = m.groupValues[1].toIntOrNull()
            val month = mesi[m.groupValues[2]]
            val year = m.groupValues[3].takeIf { it.isNotBlank() }?.toIntOrNull() ?: today.year
            if (day != null && month != null) {
                runCatching { LocalDate(year, month, day) }.getOrNull()?.let { return it }
            }
        }

        // Giorno della settimana: "lunedì", eventualmente "lunedì prossimo"
        for ((nome, giorno) in giorniSettimana) {
            if (text.contains(nome)) {
                var candidate = today
                // Avanza fino al prossimo giorno della settimana richiesto (oggi escluso)
                do {
                    candidate = candidate.plus(1, DateTimeUnit.DAY)
                } while (candidate.dayOfWeek != giorno)
                // "prossimo" = salta anche la settimana corrente
                if (text.contains("$nome prossimo") || text.contains("prossimo $nome")) {
                    candidate = candidate.plus(7, DateTimeUnit.DAY)
                }
                return candidate
            }
        }

        return null
    }

    /**
     * Riconosce un intervallo di date: "dal 11 al 30", "dall'11 al 30 marzo". Se non viene
     * indicato il mese, usa quello di [today]; se l'intervallo risulta già passato in quel mese,
     * lo sposta al mese successivo (es. "dal 1 al 5" chiesto il 20 del mese).
     */
    private fun extractDateRange(text: String, today: LocalDate): Pair<LocalDate, LocalDate>? {
        val match = Regex(
            """dall?'?\s*(\d{1,2})\s+al\s+(\d{1,2})(?:\s+(""" + mesi.keys.joinToString("|") + """))?"""
        ).find(text) ?: return null

        val giornoInizio = match.groupValues[1].toIntOrNull() ?: return null
        val giornoFine = match.groupValues[2].toIntOrNull() ?: return null
        val meseNominato = mesi[match.groupValues[3]]

        var year = today.year
        var month = meseNominato ?: today.monthNumber

        fun build(): Pair<LocalDate, LocalDate>? = runCatching {
            LocalDate(year, month, giornoInizio) to LocalDate(year, month, giornoFine)
        }.getOrNull()

        var result = build() ?: return null

        // Nessun mese esplicito e l'intervallo è già passato questo mese: prova il mese prossimo
        if (meseNominato == null && result.second < today) {
            month += 1
            if (month > 12) { month = 1; year += 1 }
            result = build() ?: result
        }

        return result
    }

    /**
     * Riconosce più giorni specifici dello stesso mese elencati con "e"/virgole, che condividono
     * un'unica menzione del mese ("il 2 e 3 dicembre", "2, 3 e 5 dicembre"): un solo giorno da
     * solo NON viene intercettato qui (resta a [extractDate], che gestisce anche l'anno esplicito
     * e non richiede una coordinazione). Il risultato è il range min-max dei giorni trovati: per
     * giorni adiacenti (2 e 3) è esatto; per giorni sparsi (2 e 5) include anche quelli di mezzo —
     * limite noto, accettabile per i comandi di cancellazione/modifica su annunci esistenti.
     */
    private fun extractGiorniMultipliMese(text: String, today: LocalDate): Pair<LocalDate, LocalDate>? {
        val match = Regex(
            """((?:\d{1,2}[\s,e]*)+)(""" + mesi.keys.joinToString("|") + """)"""
        ).find(text) ?: return null

        val giorni = Regex("""\d{1,2}""").findAll(match.groupValues[1]).mapNotNull { it.value.toIntOrNull() }.toList()
        if (giorni.size < 2) return null // un solo giorno: lascia gestire a extractDate

        val month = mesi[match.groupValues[2]] ?: return null
        var year = today.year
        val minDay = giorni.min()
        val maxDay = giorni.max()

        fun build(): Pair<LocalDate, LocalDate>? = runCatching {
            LocalDate(year, month, minDay) to LocalDate(year, month, maxDay)
        }.getOrNull()

        var result = build() ?: return null
        if (result.second < today) {
            year += 1
            result = build() ?: result
        }
        return result
    }

    /**
     * Riconosce: "dalle 8 alle 20", "dalle 8:30 alle 14:00", "dalle ore 8 alle ore 20", e la
     * variante senza "dalle/alle" a volte prodotta dal riconoscimento vocale ("8/16:25").
     */
    private fun extractTimeRange(text: String): Pair<String?, String?> {
        Regex(
            """dalle\s+(?:ore\s+)?(\d{1,2})(?:[:.](\d{2}))?\s+alle\s+(?:ore\s+)?(\d{1,2})(?:[:.](\d{2}))?"""
        ).find(text)?.let { m ->
            return formatOra(m.groupValues[1], m.groupValues[2]) to formatOra(m.groupValues[3], m.groupValues[4])
        }

        // Fallback tollerante: richiede i minuti su almeno un lato per non confondersi con una
        // data in formato giorno/mese (che non ha mai ":mm"). Es. "8/16:25".
        Regex("""(\d{1,2})(?:[:.](\d{2}))?\s*/\s*(\d{1,2})[:.](\d{2})""").find(text)?.let { m ->
            return formatOra(m.groupValues[1], m.groupValues[2]) to formatOra(m.groupValues[3], m.groupValues[4])
        }

        // Fallback tollerante: ora di fine con un trattino al posto dei due punti ("8 20-35"),
        // capita quando lo speech-to-text perde le parole "dalle"/"alle".
        Regex("""(\d{1,2})(?:[:.](\d{2}))?\s+(\d{1,2})-(\d{2})\b""").find(text)?.let { m ->
            return formatOra(m.groupValues[1], m.groupValues[2]) to formatOra(m.groupValues[3], m.groupValues[4])
        }

        // Ultimo fallback, il più permissivo: due numeri "nudi" separati solo da uno spazio,
        // senza dalle/alle/due punti/trattino. Capita quando lo speech-to-text scrive i numeri
        // come parole ("otto venti" -> "8 20" dopo la conversione fatta in normalize()) e perde
        // anche le parole di collegamento. Accettato solo se entrambi sono ore valide (0-23),
        // per rischiare il meno possibile di scambiarli per altri numeri nella frase.
        Regex("""(?<!\d)(\d{1,2})\s+(\d{1,2})(?!\d)""").find(text)?.let { m ->
            val inizio = formatOra(m.groupValues[1], "")
            val fine = formatOra(m.groupValues[2], "")
            if (inizio != null && fine != null) return inizio to fine
        }

        return null to null
    }

    private fun formatOra(ore: String, minuti: String): String? {
        val h = ore.toIntOrNull() ?: return null
        if (h !in 0..23) return null
        val m = minuti.toIntOrNull() ?: 0
        val hh = h.toString().padStart(2, '0')
        val mm = m.toString().padStart(2, '0')
        return "$hh:$mm"
    }

    /**
     * Riconosce: "25 euro", "25,50 euro", "25€", "25 euro all'ora / l'ora" e anche la forma con
     * il simbolo prima del numero ("€25", "€ 25,50" — capita spesso nella trascrizione vocale).
     *
     * Il simbolo € prima del numero è quasi sempre inequivocabile (un orario non è mai preceduto
     * da "€"), quindi lo controlliamo per primo. Il numero-poi-simbolo/parola è più ambiguo:
     * un orario detto senza i due punti ("alle 16") può stare subito prima di un "€"/"euro"
     * successivo ("...alle 16 €30" avrebbe letto "16" come importo) — lo escludiamo se il numero
     * è preceduto da "alle"/"dalle" o da un'altra cifra/":" (coda di un orario tipo "20:00").
     */
    private fun extractCompenso(text: String): Double? {
        Regex("""€\s*(\d+(?:[.,]\d{1,2})?)""").find(text)?.let {
            return it.groupValues[1].replace(",", ".").toDoubleOrNull()
        }
        Regex("""(?<![\d:])(?<!alle )(?<!dalle )(\d+(?:[.,]\d{1,2})?)\s*(?:€|euro)""").find(text)?.let {
            return it.groupValues[1].replace(",", ".").toDoubleOrNull()
        }
        return null
    }

    /**
     * Estrae un candidato di località dopo preposizioni tipiche ("a Milano", "in zona Bergamo",
     * "zona Monza"). È solo un candidato testuale: la validazione/autocompletamento contro i
     * comuni reali resta a carico del campo esistente nel form (via /api/geo/comuni/search),
     * così il parser rimane offline al 100%.
     */
    private fun extractLocationCandidate(text: String): String? {
        val match = Regex("""(?:\ba\b|\bad\b|\bin zona\b|\bzona\b|\bprovincia di\b)\s+([a-zàèéìòù'\s]{2,30}?)(?=\s+(?:dalle|il|per|con|$)|$)""")
            .find(text) ?: return null
        return match.groupValues[1].trim().takeIf { it.isNotBlank() }?.replaceFirstChar { it.uppercase() }
    }

    /** "in provincia di Bergamo", "provincia di Milano": solo per le ricerche farmacie/farmacisti. */
    private fun extractProvincia(text: String): String? {
        val match = Regex("""provincia di\s+([a-zàèéìòù'\s]{2,30}?)(?=\s+(?:dalle|il|per|con|$)|$)""")
            .find(text) ?: return null
        return match.groupValues[1].trim().takeIf { it.isNotBlank() }?.replaceFirstChar { it.uppercase() }
    }

    /** "cerca farmacie in Lombardia", "cerca farmacie in Emilia Romagna", ecc. */
    private fun extractRegione(text: String): String? {
        for ((pronuncia, canonico) in regioniMap) {
            if (text.contains(pronuncia)) return canonico
        }
        return null
    }

    /**
     * Nome proprio della farmacia, riconosciuto con un marcatore esplicito ("farmacia chiamata
     * X", "farmacia di nome X", "farmacia che si chiama X") — utile quando dopo il nome segue
     * altro testo (es. anche una città) che altrimenti finirebbe incluso nella cattura.
     */
    private fun extractNomeFarmacia(text: String): String? {
        val match = Regex("""farmaci[ae]\s+(?:chiamata|che si chiama|di nome)\s+([a-zàèéìòù0-9'\s]{2,40}?)(?=\s+(?:a\b|in\b|dalle|il|per|con|$)|$)""")
            .find(text) ?: return null
        return match.groupValues[1].trim().takeIf { it.isNotBlank() }?.replaceFirstChar { it.uppercase() }
    }

    /**
     * Comune con preposizione esplicita ("a Milano", "ad Albenga" — elisione davanti a vocale,
     * comunissima e spesso trascritta correttamente dallo STT — "in zona Bergamo", "zona Monza",
     * "in Savona").
     */
    private fun extractComuneEsplicito(text: String): String? {
        val match = Regex("""(?:\ba\b|\bad\b|\bin zona\b|\bzona\b|\bin\b)\s+([a-zàèéìòù'\s]{2,30}?)(?=\s+(?:dalle|il|per|con|$)|$)""")
            .find(text) ?: return null
        return match.groupValues[1].trim().takeIf { it.isNotBlank() }?.replaceFirstChar { it.uppercase() }
    }

    /**
     * Parola subito dopo "farmacia/farmacie" senza preposizione né marcatore ("cerca farmacia
     * Spinaci", "cerca farmacia Savona"): destinata al campo `query`, che lato server cerca sia
     * nel nome della farmacia sia nella città, quindi copre entrambe le interpretazioni possibili.
     */
    private fun extractParolaDopoFarmacia(text: String): String? {
        val match = Regex("""(?:farmacia|farmacie)\s+([a-zàèéìòù'\s]{2,30}?)(?=\s+(?:dalle|il|per|con|$)|$)""")
            .find(text) ?: return null
        return match.groupValues[1].trim().takeIf { it.isNotBlank() }?.replaceFirstChar { it.uppercase() }
    }

    /**
     * Parola subito dopo "farmacista/farmacisti" senza preposizione ("cerca farmacisti Monza"):
     * qui non c'è un campo combinato lato server, quindi resta un comune (interpretazione più
     * comune nel parlato).
     */
    private fun extractParolaDopoFarmacista(text: String): String? {
        val match = Regex("""(?:farmacista|farmaciste|farmacisti)\s+([a-zàèéìòù'\s]{2,30}?)(?=\s+(?:dalle|il|per|con|$)|$)""")
            .find(text) ?: return null
        return match.groupValues[1].trim().takeIf { it.isNotBlank() }?.replaceFirstChar { it.uppercase() }
    }
}
